import { useState, useEffect, useMemo, useRef } from "react"
import {
	CartItem,
	useTaxEstimate,
	TaxPricingPost,
	CustomerSite,
	LineItemType,
} from "../portal-apps"

const useGetTaxes = (
	cart?: CartItem[],
	customerId?: string | undefined,
	calculateTax?: boolean,
	isGuest?: boolean,
	alternateAddressId?: string | null,
	alternateAddress?: CustomerSite,
	freightEstimate?: number,
	tariffEstimate?: number,
	calculateTariffs?: boolean
) => {
	const [totalTax, setTotalTax] = useState<number>(0)
	const [tariffTax, setTariffTax] = useState<number>(0)
	const [error, setError] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState<boolean>(false)
	const fetchTaxEstimate = useTaxEstimate()

	// Use a ref to store the last payload to avoid unnecessary API calls (non reactive)
	const lastPayloadRef = useRef<string | null>(null)
	const lineItems = useMemo(() => {
		const items = cart?.map((item) => ({
			part_number: item?.part.part.partNumber,
			amount: item?.part?.clearanceItem ? Number(item?.part?.price) : item?.part?.netPrice,
			quantity: item?.quantity,
		}))
		if (tariffEstimate) {
			items?.push({
				part_number: LineItemType.TariffSurcharge,
				amount: tariffEstimate,
				quantity: 1,
			})
		}

		if (freightEstimate) {
			items?.push({
				part_number: LineItemType.Freight,
				amount: freightEstimate,
				quantity: 1,
			})
		}
		return items
	}, [cart, tariffEstimate, freightEstimate])
	// Memoize the payload to avoid unnecessary recalculations on dependencies re renders
	const payload = useMemo((): TaxPricingPost | null => {
		if (!lineItems || lineItems.length === 0 || !calculateTax) return null

		if (
			calculateTariffs &&
			!tariffEstimate &&
			lineItems.find((item) => item.part_number === LineItemType.TariffSurcharge)
		) {
			return null
		}

		if (alternateAddress) {
			return {
				shipTo: {
					line1: alternateAddress?.address1 ?? "",
					line2: alternateAddress?.address2 ?? "",
					city: alternateAddress?.city ?? "",
					region: alternateAddress?.state ?? "",
					postalCode: alternateAddress?.zip ?? "",
				},
				lines: lineItems,
			}
		} else if (isGuest) {
			if (alternateAddressId) {
				return {
					alternateAddressId,
					lines: lineItems,
				}
			}
			return null
		}
		if (!customerId) return null
		return {
			customerId,
			lines: lineItems,
		}
	}, [lineItems, calculateTax, isGuest, alternateAddress, alternateAddressId, customerId])

	useEffect(() => {
		if (!payload) return

		const payloadStr = JSON.stringify(payload)
		if (lastPayloadRef.current === payloadStr) {
			return // No change in payload, skip fetching
		}
		// Update the last payload ref to the current payload
		lastPayloadRef.current = payloadStr

		const getTaxEstimate = async () => {
			setIsLoading(true)
			try {
				const response = await fetchTaxEstimate(payload)
				const tariff = response?.data?.lines?.find(
					(item) => item.part_number === LineItemType.TariffSurcharge
				)
				setTariffTax(tariff?.tax ?? 0)
				setTotalTax(response?.data?.total_tax ?? 0)
			} catch (err) {
				console.error(err)
				setError("Error fetching tax estimate")
			} finally {
				setIsLoading(false)
			}
		}
		void getTaxEstimate()
	}, [payload, fetchTaxEstimate])

	return { totalTax, error, isLoading, tariffTax }
}

export default useGetTaxes
